import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import Checkbox from './Checkbox';
import {
    CHECKBOX_SIZE_DEFAULT,
    CHECKBOX_SIZES,
} from './constants';

import {
    createUniqueKey,
    transformOptions,
    getClassNames,
} from '../../utils';

import withTheme from '../../hocs/withTheme';
import FormControlMessage from '../FormControlMessage/FormControlMessage';
import FormGroup from '../FormGroup/FormGroup';

type CheckboxSize = (typeof CHECKBOX_SIZES)[number];

export interface CheckboxOption {
    label: string;
    value: string;
    checked?: boolean;
    position?: number;
    id?: string;
}

interface CheckboxGroupProps {
    /** Default selected value */
    defaultValue?: string[];

    /** Disable all checkboxes */
    disabled?: boolean;

    /** The name property of all checkbox children */
    name?: string | null;

    /** Options list */
    options?: CheckboxOption[];

    /** Checkbox size */
    size?: CheckboxSize;

    /** Horizontal layout */
    horizontal?: boolean;

    /** Change handler */
    onChange?: (values: string[]) => void;

    className?: string | null;

    /** Controlled value */
    value?: string[];

    children?: React.ReactNode;

    error?: string | null;

    isConditional?: boolean;

    isInQuestionnaire?: boolean;

    innerRef?: React.Ref<HTMLDivElement>;
}

const CheckboxGroupComponent: React.FC<CheckboxGroupProps> = (
    props
) => {
    const {
        defaultValue = [],
        disabled = false,
        name = null,
        options = [],
        size = CHECKBOX_SIZE_DEFAULT,
        horizontal = false,
        onChange = () => {},
        className = null,
        value = undefined,
        children = null,
        error = null,
        isConditional = false,
        isInQuestionnaire = false,
        innerRef = null,
        ...groupProps
    } = props;

    const [values, setValues] = useState<CheckboxOption[]>([]);

    const ids = useMemo(() => {
        return values.map(
            (checkboxProps) =>
                `${checkboxProps.id || checkboxProps.value}:${Math.ceil(
                    Math.random() * 1000000
                )}`
        );
    }, [values]);

    const optionsWithChecked = useMemo<CheckboxOption[]>(
        () =>
            transformOptions(options).map((option: CheckboxOption) => ({
                ...option,
                checked:
                    value?.includes(option.value) ?? false,
            })),
        [options, value]
    );

    useEffect(() => {
        setValues(optionsWithChecked);
    }, [optionsWithChecked]);

    const handleValues = (
        selectedValue: string,
        position: number | undefined,
        checked: boolean
    ): CheckboxOption[] => {
        if (!isConditional) {
            return values.map((item) =>
                item.value === selectedValue
                    ? { ...item, checked }
                    : item
            );
        }

        return values.map((item) => {
            if (
                position === 1 &&
                checked &&
                item.position !== position
            ) {
                return { ...item, checked: false };
            }

            if (
                position !== 1 &&
                checked &&
                item.position === 1
            ) {
                return { ...item, checked: false };
            }

            return item.value === selectedValue
                ? { ...item, checked }
                : item;
        });
    };

    const onChangeHandler = useCallback(
        (
            selectedValue: string,
            position: number | undefined,
            checked: boolean
        ) => {
            const mappedValue = handleValues(
                selectedValue,
                position,
                checked
            );

            setValues(mappedValue);

            onChange(
                mappedValue
                    .filter((item) => item.checked)
                    .map((item) => item.value)
            );
        },
        [onChange, values, isConditional]
    );

    const childrenToRender = useMemo(() => {
        return values.length
            ? values.map((checkboxProps, index) => (
                  <Checkbox
                      ref={undefined}
                      size={size}
                      {...checkboxProps}
                      key={createUniqueKey(
                          index,
                          checkboxProps.label
                      )}
                      id={ids[index]}
                      error={error ?? undefined}
                      isInQuestionnaire={isInQuestionnaire}
                      defaultChecked={defaultValue.includes(
                          checkboxProps.value
                      )}
                      onChange={(event) =>
                          onChangeHandler(
                              checkboxProps.value,
                              checkboxProps.position,
                              event.target.checked
                          )
                      }
                  />
              ))
            : children;
    }, [
        values,
        children,
        innerRef,
        size,
        ids,
        error,
        isInQuestionnaire,
        defaultValue,
        onChangeHandler,
    ]);

    return (
        <FormGroup error={error ?? undefined}>
            <div
                {...groupProps}
                role="group"
                className={getClassNames(
                    className,
                    'checkbox-group',
                    {
                        'checkbox-group-horizontal':
                            horizontal,
                    }
                )}
            >
                {childrenToRender}

                {error && (
                    <FormControlMessage>
                        {error}
                    </FormControlMessage>
                )}
            </div>
        </FormGroup>
    );
};

const CheckboxGroup = forwardRef<
    HTMLDivElement,
    CheckboxGroupProps
>((props, ref) =>
    withTheme(CheckboxGroupComponent)({
        ...props,
        innerRef: ref,
        className: props.className ?? undefined,
    })
);

export default CheckboxGroup;
